// What Investigate has made of one scope: the cases it folded the findings
// into, what its digs found (notes, corrections, related sessions), the
// findings it checked itself where the backend had not, the standards it
// asked here, the reader's priorities and the standard edits it suggests.
// Kept in this browser for the signed-in reader, one entry per scope, the
// most recently used few; like the workspaces it hands cases to, it is the
// reader's own and is not shared.
//
// A case: { id, title, account, findings: [id], at, by: "investigate" |
// "you", matches: [priority], dig?: { status, at, costUsd, question,
// needsPerson, why, error }, notes: [note], related: [{ session, why }] }.
// One the case inbox keeps (lib/caseInbox.js) also says how it came to need
// someone (`origin`: standard | audit | rare), the reader's decision on it
// (`decision`: { kind: let_go | taken, at }), for one an audit sampled, how it
// was picked (`audit`: { kind: random | rare, group, at }), and the kinds of
// act its findings name (`kinds`), to know a case like it again.
// A note: { id, kind: matters | doesnt | missed | corrects | note, text,
// finding?, session?, messageId?, quote?, source?, by, at }.
import { useMemo, useSyncExternalStore } from "react";
import { onAuthIdentityChange } from "./authTokenStore.js";
import { browserStorageKey } from "./browserStorageScope.js";
import { CLAIM_KINDS } from "./triageLite.js";

const PREFIX = "origin-investigate-v1";
const VERSION = 1;
// Scopes kept, the most recently written.
export const KEPT_SCOPES = 12;
export const CASES_MAX = 400;
export const NOTES_PER_CASE = 24;
export const RELATED_MAX = 12;
export const CHECKS_MAX = 600;
export const PRIORITIES_MAX = 8;
export const SUGGESTIONS_MAX = 6;
export const NOTE_KINDS = ["matters", "doesnt", "missed", "corrects", "note"];
const TITLE_MAX = 140;
const ACCOUNT_MAX = 900;
const TEXT_MAX = 600;
const QUOTE_MAX = 400;
const PRIORITY_MAX = 160;
const STANDARD_TEXT_MAX = 8000;
const OWNER_WAIT_TRIES = 30;

const str = (v) => (v == null ? "" : String(v));
const lower = (v) => str(v).trim().toLowerCase();
const clip = (v, n) => {
    const t = str(v).trim();
    return t.length > n ? `${t.slice(0, n - 1).trimEnd()}\u2026` : t;
};
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const ids = (list, max = 5000) => [...new Set((Array.isArray(list) ? list : []).map(lower).filter(Boolean))].slice(0, max);
export const newId = (prefix) => `${prefix}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 7)}`;

function cleanNote(n) {
    if (!n || typeof n !== "object" || !str(n.text).trim()) return null;
    const kind = NOTE_KINDS.includes(n.kind) ? n.kind : "note";
    return {
        id: str(n.id) || newId("n:"),
        kind,
        text: clip(n.text, TEXT_MAX),
        finding: n.finding ? lower(n.finding) : null,
        session: n.session ? lower(n.session) : null,
        // As the record writes it: the board matches a note's message
        // exactly, and message ids are not all lower-case.
        messageId: n.messageId ? str(n.messageId).trim() : null,
        quote: n.quote ? clip(n.quote, QUOTE_MAX) : null,
        source: n.source === "machine" || n.source === "session" ? n.source : null,
        by: n.by === "you" ? "you" : "investigate",
        at: num(n.at),
    };
}

function cleanDig(d) {
    if (!d || typeof d !== "object") return null;
    return {
        status: ["done", "failed", "stopped"].includes(d.status) ? d.status : "done",
        at: num(d.at),
        costUsd: num(d.costUsd),
        question: clip(d.question, PRIORITY_MAX * 2),
        needsPerson: ["yes", "no", "unsure"].includes(d.needsPerson) ? d.needsPerson : null,
        why: clip(d.why, TEXT_MAX),
        error: clip(d.error, 300),
        reads: num(d.reads),
    };
}

export const CASE_ORIGINS = ["standard", "audit", "rare"];
const KINDS_PER_CASE = 8;
const KIND_MAX = 120;
export const DECISION_KINDS = ["let_go", "taken"];
export const AUDIT_KINDS = ["random", "rare"];
const cleanDecision = (d) => (d && DECISION_KINDS.includes(d.kind) ? { kind: d.kind, at: num(d.at) } : null);
const cleanAudit = (a) => (a && AUDIT_KINDS.includes(a.kind) ? { kind: a.kind, group: clip(a.group, 40), at: num(a.at) } : null);

function cleanCase(c) {
    if (!c || typeof c !== "object" || !str(c.id)) return null;
    const findings = ids(c.findings);
    if (!findings.length) return null;
    const decision = cleanDecision(c.decision);
    const audit = cleanAudit(c.audit);
    const kinds = [...new Set((Array.isArray(c.kinds) ? c.kinds : []).map((k) => clip(k, KIND_MAX)).filter(Boolean))].slice(0, KINDS_PER_CASE);
    return {
        ...(CASE_ORIGINS.includes(c.origin) ? { origin: c.origin } : {}),
        ...(decision ? { decision } : {}),
        ...(audit ? { audit } : {}),
        ...(kinds.length ? { kinds } : {}),
        id: str(c.id),
        title: clip(c.title, TITLE_MAX),
        account: clip(c.account, ACCOUNT_MAX),
        findings,
        at: num(c.at),
        by: c.by === "you" ? "you" : "investigate",
        matches: (Array.isArray(c.matches) ? c.matches : []).map((m) => clip(m, PRIORITY_MAX)).filter(Boolean).slice(0, PRIORITIES_MAX),
        dig: cleanDig(c.dig),
        notes: (Array.isArray(c.notes) ? c.notes : []).map(cleanNote).filter(Boolean).slice(-NOTES_PER_CASE),
        related: (Array.isArray(c.related) ? c.related : [])
            .filter((r) => r && lower(r.session))
            .map((r) => ({ session: lower(r.session), why: clip(r.why, TEXT_MAX) }))
            .slice(0, RELATED_MAX),
    };
}

function cleanCorrection(k) {
    const claims = {};
    for (const kind of CLAIM_KINDS) if (k?.claims?.[kind]) claims[kind] = str(k.claims[kind]);
    if (!Object.keys(claims).length) return null;
    return { claims, why: clip(k.why, TEXT_MAX), quote: clip(k.quote, QUOTE_MAX), source: k.source === "machine" ? "machine" : "session", caseId: k.caseId ? str(k.caseId) : null, at: num(k.at) };
}

function cleanCheck(v) {
    if (!v || typeof v !== "object" || !str(v.verdict)) return null;
    return {
        findingId: lower(v.findingId),
        verdict: str(v.verdict),
        failedClaim: str(v.failedClaim),
        correctedClaim: clip(v.correctedClaim, TEXT_MAX),
        why: clip(v.why, TEXT_MAX),
        claims: CLAIM_KINDS.map((kind) => ({ kind, status: str((v.claims || []).find((c) => c?.kind === kind)?.status) })),
        evidence: (Array.isArray(v.evidence) ? v.evidence : []).slice(0, 6).map((e, i) => ({ id: str(e?.id) || `q${i}`, text: clip(e?.text, QUOTE_MAX), messageId: e?.messageId ? lower(e.messageId) : null })).filter((e) => e.text),
        citationLocated: v.citationLocated !== false,
        costUsd: num(v.costUsd),
        at: num(v.at),
        here: true,
    };
}

function cleanAsked(a) {
    if (!a || typeof a !== "object" || !str(a.key)) return null;
    const answers = (Array.isArray(a.answers) ? a.answers : [])
        .filter((x) => x && str(x.digest) && ["met", "not_met", "unconfirmed", "error"].includes(x.outcome))
        .map((x) => ({ digest: str(x.digest), name: clip(x.name, 200), version: num(x.version) || 1, outcome: x.outcome, why: clip(x.why, TEXT_MAX) }));
    return { key: str(a.key), answers, at: num(a.at) };
}

function cleanSuggestion(s) {
    if (!s || typeof s !== "object" || !str(s.text).trim()) return null;
    return {
        id: str(s.id) || newId("s:"),
        kind: s.kind === "edit" ? "edit" : "new",
        standardId: s.standardId ? str(s.standardId) : null,
        name: clip(s.name, 200),
        text: str(s.text).slice(0, STANDARD_TEXT_MAX),
        why: clip(s.why, TEXT_MAX),
        catches: ids(s.catches, 60),
        spares: ids(s.spares, 60),
        at: num(s.at),
        tried: s.tried && typeof s.tried === "object" ? {
            caught: num(s.tried.caught), of: num(s.tried.of), spared: num(s.tried.spared), ofSpared: num(s.tried.ofSpared), costUsd: num(s.tried.costUsd), at: num(s.tried.at),
        } : null,
        dismissed: s.dismissed === true,
        added: s.added ? str(s.added) : null,
    };
}

const cleanMap = (raw, clean, max) => {
    const entries = Object.entries(raw && typeof raw === "object" ? raw : {})
        .map(([id, v]) => [lower(id), clean(v)])
        .filter(([id, v]) => id && v)
        .sort((a, b) => num(b[1].at) - num(a[1].at))
        .slice(0, max);
    return Object.fromEntries(entries);
};

export function cleanInvestigation(raw) {
    const cases = (Array.isArray(raw?.cases) ? raw.cases : []).map(cleanCase).filter(Boolean)
        .sort((a, b) => b.at - a.at).slice(0, CASES_MAX);
    // A finding sits in one case: the newest case to take it keeps it. An
    // audit's record is its sample, and keeps its findings whoever else
    // takes them.
    const taken = new Set();
    const kept = [];
    for (const c of cases) {
        if (c.audit) { kept.push(c); continue; }
        const findings = c.findings.filter((id) => !taken.has(id) && (taken.add(id), true));
        if (findings.length) kept.push(findings.length === c.findings.length ? c : { ...c, findings });
    }
    return {
        v: VERSION,
        at: num(raw?.at),
        cases: kept,
        corrections: cleanMap(raw?.corrections, cleanCorrection, CHECKS_MAX),
        checks: cleanMap(raw?.checks, cleanCheck, CHECKS_MAX),
        asked: cleanMap(raw?.asked, cleanAsked, CHECKS_MAX),
        priorities: (Array.isArray(raw?.priorities) ? raw.priorities : []).map((p) => clip(p, PRIORITY_MAX)).filter(Boolean).slice(0, PRIORITIES_MAX),
        suggestions: (Array.isArray(raw?.suggestions) ? raw.suggestions : []).map(cleanSuggestion).filter(Boolean).slice(-SUGGESTIONS_MAX),
        organizedAt: num(raw?.organizedAt),
        spentUsd: num(raw?.spentUsd),
    };
}

const EMPTY = Object.freeze(cleanInvestigation(null));
const ownerPrefix = () => browserStorageKey(PREFIX);
const scopeOf = (scopeKey) => str(scopeKey) || "org";

// What each scope holds, read once per reader: `${owner}|${scope}`.
const held = new Map();
const subscribers = new Set();
let version = 0;
function emit() {
    version += 1;
    for (const fn of subscribers) fn();
}

// Right after a reload the reader is not known yet: look again each second,
// and draw again once they are.
let waiting = null;
function waitForOwner() {
    if (waiting || typeof setTimeout !== "function") return;
    let tries = 0;
    const look = () => {
        tries += 1;
        if (ownerPrefix()) { waiting = null; held.clear(); emit(); }
        else if (tries < OWNER_WAIT_TRIES) waiting = setTimeout(look, 1000);
        else waiting = null;
    };
    waiting = setTimeout(look, 1000);
}

// The earlier Investigate pass kept its placements in this browser, one entry
// per scope and up to thousands of findings each; nothing reads them now, and
// they share the storage this keeps cases in. Cleared once per page, for the
// reader signed in.
const OLD_LEDGER = "origin.triage.v2";
let swept = null;
function sweepOldLedger() {
    const prefix = browserStorageKey(OLD_LEDGER);
    const storage = globalThis.localStorage;
    if (!prefix || !storage || swept === prefix) return;
    swept = prefix;
    try {
        const old = [];
        for (let i = 0; i < storage.length; i++) {
            const key = storage.key(i);
            if (key && key.startsWith(`${prefix}:`)) old.push(key);
        }
        for (const key of old) storage.removeItem(key);
    } catch { /* a storage that cannot be listed keeps them */ }
}

function entryOf(scope, owner = ownerPrefix()) {
    const k = `${owner ?? ""}|${scope}`;
    const had = held.get(k);
    if (had) return had;
    let entry = EMPTY;
    if (owner) {
        sweepOldLedger();
        try {
            const raw = globalThis.localStorage?.getItem(`${owner}:${scope}`);
            if (raw) entry = cleanInvestigation(JSON.parse(raw));
        } catch { /* unreadable: it starts empty */ }
    } else {
        waitForOwner();
    }
    held.set(k, entry);
    return entry;
}

function keptScopes(storage, owner) {
    const out = [];
    try {
        for (let i = 0; i < storage.length; i++) {
            const key = storage.key(i);
            if (!key || !key.startsWith(`${owner}:`)) continue;
            let at = 0;
            try { at = num(JSON.parse(storage.getItem(key) || "null")?.at); } catch { at = 0; }
            out.push({ key, at });
        }
    } catch { /* a storage without key(): nothing to make room from */ }
    return out.sort((a, b) => a.at - b.at);
}

// Kept for the scope, making room when it is a new one past the cap, or when
// the browser refuses the write (its quota is shared with the rest of the
// origin): other scopes written longest ago go first, then this scope's
// oldest cases.
function persist(owner, scope, entry) {
    const storage = globalThis.localStorage;
    if (!storage) return entry;
    const key = `${owner}:${scope}`;
    const others = () => keptScopes(storage, owner).filter((s) => s.key !== key);
    if (storage.getItem(key) == null) {
        const old = others();
        for (const s of old.slice(0, Math.max(0, old.length - (KEPT_SCOPES - 1)))) storage.removeItem(s.key);
    }
    let value = entry;
    for (;;) {
        try {
            storage.setItem(key, JSON.stringify(value));
            return value;
        } catch {
            const oldest = others()[0];
            if (oldest) {
                storage.removeItem(oldest.key);
                held.delete(`${owner}|${oldest.key.slice(owner.length + 1)}`);
                continue;
            }
            if (value.cases.length > 20) {
                value = { ...value, cases: value.cases.slice(0, Math.floor(value.cases.length * 0.75)) };
                continue;
            }
            console.warn("[investigate] not kept: this browser's storage is full");
            return value;
        }
    }
}

/** One scope's investigation. */
export function investigationOf(scopeKey) {
    return entryOf(scopeOf(scopeKey));
}

/**
 * Changes one scope's investigation: `fn` gets what is held and returns the
 * next state (or null to leave it). Returns what is held after.
 */
export function changeInvestigation(scopeKey, fn) {
    const scope = scopeOf(scopeKey);
    const owner = ownerPrefix();
    // Another tab may have written this scope since it was read here.
    if (owner) held.delete(`${owner}|${scope}`);
    const cur = entryOf(scope, owner);
    const next = fn(cur);
    if (!next || next === cur) return cur;
    let entry = cleanInvestigation({ ...next, at: Date.now() });
    if (owner) entry = persist(owner, scope, entry);
    held.set(`${owner ?? ""}|${scope}`, entry);
    emit();
    return entry;
}

// -- writes -------------------------------------------------------------------

// Cases written by an organize pass: each takes its findings from whatever
// case held them before, and a case left with none is gone.
export function putCases(scopeKey, cases, { replace = [] } = {}) {
    const fresh = (cases || []).map((c) => cleanCase({ ...c, at: c.at || Date.now() })).filter(Boolean);
    if (!fresh.length && !replace.length) return investigationOf(scopeKey);
    return changeInvestigation(scopeKey, (cur) => {
        const drop = new Set(replace);
        const moved = new Set(fresh.flatMap((c) => c.findings));
        const freshIds = new Set(fresh.map((c) => c.id));
        const kept = cur.cases
            .filter((c) => !drop.has(c.id) && !freshIds.has(c.id))
            .map((c) => (c.audit ? c : { ...c, findings: c.findings.filter((id) => !moved.has(id)) }))
            .filter((c) => c.findings.length);
        return { ...cur, cases: [...fresh, ...kept] };
    });
}

export function updateCase(scopeKey, caseId, fn) {
    return changeInvestigation(scopeKey, (cur) => {
        const at = cur.cases.findIndex((c) => c.id === caseId);
        if (at < 0) return null;
        const next = fn(cur.cases[at]);
        if (!next) return null;
        const cases = [...cur.cases];
        cases[at] = { ...next, at: Date.now() };
        return { ...cur, cases };
    });
}

export function addCaseNotes(scopeKey, caseId, notes) {
    const fresh = (notes || []).map((n) => cleanNote({ ...n, at: n.at || Date.now() })).filter(Boolean);
    if (!fresh.length) return investigationOf(scopeKey);
    return updateCase(scopeKey, caseId, (c) => ({ ...c, notes: [...c.notes, ...fresh].slice(-NOTES_PER_CASE) }));
}

export function removeCaseNote(scopeKey, caseId, noteId) {
    return updateCase(scopeKey, caseId, (c) => (c.notes.some((n) => n.id === noteId) ? { ...c, notes: c.notes.filter((n) => n.id !== noteId) } : null));
}

// A finding moved by the reader to another case, or into one of its own.
export function moveFinding(scopeKey, findingId, toCaseId, { title = "" } = {}) {
    const id = lower(findingId);
    return changeInvestigation(scopeKey, (cur) => {
        const from = cur.cases.find((c) => c.findings.includes(id));
        if (from?.id === toCaseId) return null;
        let cases = cur.cases.map((c) => (c === from ? { ...c, findings: c.findings.filter((x) => x !== id) } : c)).filter((c) => c.findings.length);
        const target = toCaseId ? cases.find((c) => c.id === toCaseId) : null;
        if (target) cases = cases.map((c) => (c === target ? { ...c, findings: [...c.findings, id], at: Date.now() } : c));
        else cases = [{ id: newId("c:"), title: clip(title, TITLE_MAX), account: "", findings: [id], at: Date.now(), by: "you", matches: [], dig: null, notes: [], related: [] }, ...cases];
        return { ...cur, cases };
    });
}

export function putCorrections(scopeKey, corrections) {
    const entries = Object.entries(corrections || {}).map(([id, k]) => [lower(id), cleanCorrection({ ...k, at: k?.at || Date.now() })]).filter(([id, k]) => id && k);
    if (!entries.length) return investigationOf(scopeKey);
    return changeInvestigation(scopeKey, (cur) => ({ ...cur, corrections: { ...cur.corrections, ...Object.fromEntries(entries) } }));
}

export function dropCorrection(scopeKey, findingId) {
    const id = lower(findingId);
    return changeInvestigation(scopeKey, (cur) => {
        if (!cur.corrections[id]) return null;
        const { [id]: _gone, ...rest } = cur.corrections;
        return { ...cur, corrections: rest };
    });
}

export function putChecks(scopeKey, checks) {
    const entries = Object.entries(checks || {}).map(([id, v]) => [lower(id), cleanCheck({ ...v, findingId: id, at: v?.at || Date.now() })]).filter(([id, v]) => id && v);
    if (!entries.length) return investigationOf(scopeKey);
    return changeInvestigation(scopeKey, (cur) => ({ ...cur, checks: { ...cur.checks, ...Object.fromEntries(entries) } }));
}

export function putAsked(scopeKey, asked) {
    const entries = Object.entries(asked || {}).map(([id, a]) => [lower(id), cleanAsked({ ...a, at: a?.at || Date.now() })]).filter(([id, a]) => id && a);
    if (!entries.length) return investigationOf(scopeKey);
    return changeInvestigation(scopeKey, (cur) => ({ ...cur, asked: { ...cur.asked, ...Object.fromEntries(entries) } }));
}

export function setPriorities(scopeKey, priorities) {
    return changeInvestigation(scopeKey, (cur) => ({ ...cur, priorities: (priorities || []).map((p) => clip(p, PRIORITY_MAX)).filter(Boolean) }));
}

export function putSuggestions(scopeKey, suggestions, { replace = false } = {}) {
    const fresh = (suggestions || []).map((s) => cleanSuggestion({ ...s, at: s.at || Date.now() })).filter(Boolean);
    return changeInvestigation(scopeKey, (cur) => ({ ...cur, suggestions: replace ? fresh : [...cur.suggestions.filter((s) => !fresh.some((f) => f.id === s.id)), ...fresh] }));
}

export function updateSuggestion(scopeKey, id, patch) {
    return changeInvestigation(scopeKey, (cur) => {
        const at = cur.suggestions.findIndex((s) => s.id === id);
        if (at < 0) return null;
        const suggestions = [...cur.suggestions];
        suggestions[at] = { ...suggestions[at], ...patch };
        return { ...cur, suggestions };
    });
}

export function noteSpend(scopeKey, usd) {
    const add = num(usd);
    if (!(add > 0)) return investigationOf(scopeKey);
    return changeInvestigation(scopeKey, (cur) => ({ ...cur, spentUsd: cur.spentUsd + add }));
}

export function markOrganized(scopeKey) {
    return changeInvestigation(scopeKey, (cur) => ({ ...cur, organizedAt: Date.now() }));
}

/** Whether an investigation holds anything Investigate made or the reader set. */
export function hasInvestigationWork(inv) {
    return Boolean(inv && (inv.cases.length || inv.priorities.length || inv.suggestions.length || inv.spentUsd > 0 || inv.organizedAt
        || Object.keys(inv.corrections).length || Object.keys(inv.checks).length || Object.keys(inv.asked).length));
}

/**
 * Sets one scope's investigation aside for a fresh one: its cases and what
 * digs noted, its corrections and checks, the standards asked here, the
 * reader's priorities and the edits it suggested. The findings and what the
 * backend made of them are untouched.
 */
export function resetInvestigation(scopeKey) {
    return changeInvestigation(scopeKey, (cur) => (hasInvestigationWork(cur) ? {} : null));
}

/** Forgets every scope this reader keeps here, for the chat-data wipe. */
export function clearStoredInvestigations(tenantId = null) {
    const owner = browserStorageKey(PREFIX, tenantId);
    const storage = globalThis.localStorage;
    if (owner && storage) for (const s of keptScopes(storage, owner)) storage.removeItem(s.key);
    held.clear();
    emit();
}

function subscribe(fn) {
    subscribers.add(fn);
    return () => subscribers.delete(fn);
}

// Harness seam (lanes-harness &earlier=1): one scope's investigation held as
// given, its `at` kept, as a reload finds one this browser kept earlier.
export function seedInvestigation(scopeKey, raw) {
    held.set(`${ownerPrefix() ?? ""}|${scopeOf(scopeKey)}`, cleanInvestigation(raw));
    emit();
}

onAuthIdentityChange(() => { held.clear(); emit(); });
if (typeof window !== "undefined") {
    window.addEventListener("storage", (e) => {
        if (e.key == null || e.key.startsWith(PREFIX)) { held.clear(); emit(); }
    });
}

// Test seam: what this page has read and swept, forgotten.
export function _resetInvestigateStoreForTests() {
    held.clear();
    swept = null;
}

/** One scope's investigation, as a hook. */
export function useInvestigation(scopeKey) {
    const v = useSyncExternalStore(subscribe, () => version, () => version);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    return useMemo(() => investigationOf(scopeKey), [scopeKey, v]);
}
